<template>
  <section class="relative bg-brand-dark min-h-screen flex flex-col px-4 py-12 pt-24 text-brand-cream paper-texture overflow-hidden">
    <SketchDoodle type="star" :size="16" top="8%" left="6%" motion="float" :opacity="0.5" hide-on-mobile />
    <SketchDoodle type="spark" :size="20" top="10%" right="10%" motion="drift" :opacity="0.4" hide-on-mobile />
    <SketchDoodle type="squiggle" :size="40" bottom="8%" left="12%" :opacity="0.35" hide-on-mobile />
    <SketchDoodle type="code" :size="26" bottom="12%" right="6%" :rotate="8" :opacity="0.35" hide-on-mobile />

    <div class="max-w-7xl w-full mx-auto relative z-10">
      <div class="text-center mb-2 relative">
        <p class="sketch-dev text-sm mb-2" aria-hidden="true">// selected work</p>
        <h2 class="text-3xl md:text-4xl font-extrabold text-brand-cream tracking-tight">
          <span class="sketch-underline">Projects</span>
        </h2>
        <span
          class="sketch-note absolute -top-1 right-[12%] rotate-6 hidden md:block"
          aria-hidden="true"
        >
          my favorites ★
        </span>
      </div>

      <SketchDivider />

      <div class="relative">
        <!-- Accent doodle near first featured cluster -->
        <SketchDoodle
          type="arrow-ur"
          :size="28"
          top="-0.5rem"
          left="-0.25rem"
          motion="arrow"
          :opacity="0.55"
          hide-on-mobile
        />

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
          <ProjectCard
            v-for="(project, index) in projects"
            :key="project.id"
            :title="project.title"
            :description="project.description"
            :technologies="project.technologies"
            :github="project.github"
            :label="project.label"
            :featured="index < 3"
            :note="projectNotes[project.id] || ''"
          />
        </div>
      </div>

      <p class="sketch-note text-center mt-10" aria-hidden="true">
        more on github ↗ · always tinkering
      </p>
    </div>
  </section>
</template>

<script>
import ProjectCard from "./ProjectCard.vue";
import SketchDoodle from "./SketchDoodle.vue";
import SketchDivider from "./SketchDivider.vue";

export default {
  components: { ProjectCard, SketchDoodle, SketchDivider },
  data() {
    return {
      // Tiny editorial notes — decorative only, keyed by project id
      projectNotes: {
        3: "✦ highlight",
        5: "rag magic",
        8: "building this",
      },
      projects: [
        {
          id: 13,
          label: "Desktop App",
          title: "Neural",
          description: "An AI-powered terminal built with Rust (Tauri) and React that turns natural language into shell commands, streams output in real time, suggests fixes for errors, and runs multi-step workflows — with project-aware context and secret redaction.",
          technologies: ["Rust", "Tauri", "React", "SQLite", "OpenAI"],
          github: "https://github.com/BlenBizuayehu/neuralTest",
        },
        {
          id: 12,
          label: "Mobile",
          title: "Sapphire Notetaker",
          description: "An AI-powered note-taking mobile app that turns spoken lectures and meetings into structured notes — with real-time recording, speech-to-text, AI-generated key points, course organization, and smart search across iOS and Android.",
          technologies: ["Flutter", "Django", "MongoDB", "OpenAI"],
          github: "https://github.com/Geliladawit/Sapphire_Note_Taker",
        },
        {
          id: 3,
          label: "AI/ML",
          title: "Kara Solutions ELT",
          description: "Kara Solutions ELT is a modular pipeline for extracting, loading, and transforming data from Telegram channels into a PostgreSQL database for analytics and reporting. The project leverages Docker for reproducible environments, dbt for analytics engineering, and Python for data collection and orchestration",
          technologies: ["Python", "Pandas", "Numpy", "TextBlob"],
          github: "https://github.com/b-isry/Ethio-medical",
        },
        {
          id: 4,
          label: "AI/ML",
          title: "Adey-fraud-detection",
          description: "a fraud detection system for e-commerce and bank credit transactions at Adey Innovations Inc.",
          technologies: ["pandas","numpy", 'scikit-learn', 'imblearn', 'matplotlib', 'seaborn'],
          github: "https://github.com/b-isry/Adey-fraud-detection"
        },
        {
          id: 5,
          label: "AI/ML",
          title: "CreditRust RAG Chatbot",
          description: "A Retrieval-Augmented Generation (RAG) chatbot for exploring and searching consumer complaint narratives using semantic search and vector databases.",
          technologies: ["pandas","numpy", 'scikit-learn', 'imblearn', 'matplotlib', 'gradio'],
          github: "https://github.com/b-isry/crediTrust"
        },
        {
          id: 6,
          label: "AI/ML",
          title: "Amharic E-commerce Data Extractor",
          description: "This project is designed to extract, preprocess, and prepare Amharic e-commerce data for manual annotation, particularly for Named Entity Recognition (NER) tasks using the CoNLL format.",
          technologies: ["pandas","numpy", 'scikit-learn', 'imblearn', 'matplotlib', 'gradio'],
          github: "https://github.com/b-isry/amharic_ecommerce_data_extractor"
        },
        {
          id: 7,
          label: "AI/ML",
          title: "News Sentiment and Stock Moves",
          description: "This project analyzes financial news headlines to understand their impact on stock prices.",
          technologies: ["Python", "Pandas", "Numpy", "TextBlob"],
          github: "https://github.com/b-isry/financial-news-sentiment-analysis",
        },
        {
          id: 8,
          label: "Tools",
          title: "DevPort",
          description: "DevPort is a next-generation dependency engine that virtualizes node_modules. It treats your dependencies as a single, versioned artifact tied directly to your git commits and stored in a shared cloud cache (like S3).",
          technologies: ["Golang"],
          github: "https://github.com/b-isry/DevPort",
        },
        {
          id: 9,
          label: "Tools",
          title: "URL manager",
          description: "A minimal Chrome extension for managing URLs with a popup interface and keyboard shortcut.",
          technologies: ["HTML", "CSS", "Java script"],
          github: "https://github.com/b-isry/Username_Manager",
        },
        {
          id: 10,
          label: "Tools",
          title: "RepoCleaner",
          description: "a Python-based GitHub App that handles webhook events and automates repository management tasks, such as deleting empty repositories.",
          technologies: ["FastAPI", "PyGithub", "ngrok"],
          github: "https://github.com/b-isry/RepoCleaner",
        },
        {
          id: 11,
          label: "Tools",
          title: "GitSafe",
          description: "GitSafe is a tool that helps you manage and backup your Git repositories. It identifies stale repositories and creates backups of them in Google Drive.",
          technologies: ["Golang"],
          github: "https://github.com/b-isry/GitSafe"
        },
        {
          id: 1,
          label: "Backend",
          title: "Afro-Vintage API Service",
          description: "This is the backend service for the Afro Vintage platform — built with Go (Gin), MongoDB, and Clean Architecture. It powers both the mobile (Flutter) and web (Next.js) apps.",
          technologies: ["Golang", "Gin", "MongoDB"],
          github: "https://github.com/A2SV/A2SV-2025-Internship-Afro-Vintage/tree/main/backend/afro-vintage-backend",
        },
        {
          id: 2,
          label: "Backend",
          title: "Grocery management system",
          description: "The Grocery Management System API is a RESTful API designed to manage grocery items and interact with product data from the OpenFoodFacts Product Database. It provides CRUD operations for grocery items and allows users to search for products by name, brand, or nutrition grade.",
          technologies: ["Golang", "MongoDB"],
          github: "https://github.com/A2SV/A2SV-2025-Internship-Assessment/tree/feat-Bisrat-backend/backend/grocery-api",
        },
      ],
    };
  },
};
</script>
